/** Original low-poly engineering objects, rasterized into shaded pixel images. */
export type Point = [number, number, number]
export type Segment = [Point, Point]
export interface Face { points: Point[]; tone: number }
export interface EngineeringModel { faces: Face[]; segments: Segment[] }
const TAU = Math.PI * 2

type Line = (a: Point, b: Point) => void
type Surface = (points: Point[], tone?: number) => void
function sketch(build: (line: Line, face: Surface) => void): EngineeringModel {
  const segments: Segment[] = []
  const faces: Face[] = []
  build((a, b) => segments.push([a, b]), (points, tone = 1) => faces.push({ points, tone }))
  return { faces, segments }
}

function loop(line: Line, points: Point[]) {
  points.forEach((point, i) => line(point, points[(i + 1) % points.length]))
}
function box(line: Line, face: Surface, x: number, y: number, z: number, w: number, h: number, d: number) {
  const a: Point[] = [[x, y, z], [x + w, y, z], [x + w, y + h, z], [x, y + h, z]]
  const b = a.map(([px, py]) => [px, py, z + d] as Point)
  loop(line, a); loop(line, b)
  face(a); face(b)
  a.forEach((point, i) => {
    const next = (i + 1) % a.length
    line(point, b[i])
    face([point, a[next], b[next], b[i]])
  })
}
function ring(line: Line, face: Surface, inner: number, outer: number, front: number, back: number) {
  const point = (radius: number, i: number, z: number): Point => [radius * Math.cos(i * TAU / 48), radius * Math.sin(i * TAU / 48), z]
  for (let i = 0; i < 48; i++) {
    for (const z of [front, back]) {
      const a = point(inner, i, z), b = point(outer, i, z)
      const c = point(outer, i + 1, z), d = point(inner, i + 1, z)
      line(a, d); line(b, c); face([a, b, c, d])
    }
    for (const r of [inner, outer]) face([point(r, i, front), point(r, i + 1, front), point(r, i + 1, back), point(r, i, back)])
  }
}

function beam(line: Line, face: Surface, a: Point, b: Point) {
  line(a, b)
  // A narrow solid web, not just a wire: the truss remains legible after dithering.
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const length = Math.hypot(dx, dy) || 1
  const offset: Point = [-dy / length * 2.5, dx / length * 2.5, 0]
  const shift = (p: Point, sign: number): Point => p.map((v, i) => v + sign * offset[i]) as Point
  face([shift(a, -1), shift(a, 1), shift(b, 1), shift(b, -1)])
}

const bridge = sketch((edge, face) => {
  const line: Line = (a, b) => beam(edge, face, a, b)
  box(line, face, -165, 30, -40, 330, 9, 80)
  for (const z of [-36, 36]) {
    line([-165, 27, z], [165, 27, z])
    line([-110, -55, z], [110, -55, z])
    line([-165, 27, z], [-110, -55, z])
    line([110, -55, z], [165, 27, z])
    for (let i = 0; i < 5; i++) {
      const x = -110 + i * 55
      line([x, 27, z], [x, -55, z])
      if (i < 4) line([x, i % 2 ? -55 : 27, z], [x + 55, i % 2 ? 27 : -55, z])
    }
  }
  for (let x = -110; x <= 110; x += 55) {
    line([x, -55, -36], [x, -55, 36])
    if (x < 110) line([x, -55, -36], [x + 55, -55, 36])
  }
  for (const x of [-125, 110]) box(line, face, x, 39, -32, 15, 58, 64)
  for (let x = -160; x < 165; x += 22) line([x, 30, -40], [x, 30, 40])
})

const circuit = sketch((line, face) => {
  box(line, (points) => face(points, .45), -130, 16, -90, 260, 5, 180)
  box(line, face, -42, -12, -38, 84, 28, 76)
  box(line, face, -29, -15, -25, 58, 3, 50)
  for (let i = 0; i < 10; i++) {
    const x = -34 + i * 7.5
    for (const side of [-1, 1]) {
      line([x, 0, side * 38], [x, 0, side * 46])
      line([x, 0, side * 46], [x, 15, side * 49])
      line([x, 15, side * 49], [x + side * 14, 15, side * 65])
      line([x + side * 14, 15, side * 65], [x + side * 14, 15, side * 82])
      line([side * 42, 0, x], [side * 53, 0, x])
      line([side * 53, 0, x], [side * 60, 15, x])
      line([side * 60, 15, x], [side * 114, 15, x])
    }
  }
  for (const x of [-112, 78]) {
    for (const z of [-70, 53]) box(line, face, x, 0, z, 30, 15, 14)
  }
  for (let i = 0; i < 14; i++) box(line, face, -110 + i * 16, 14, 81, 7, 2, 8)
})

const turbine = sketch((line, face) => {
  ring(line, face, 13, 34, -15, 15)
  ring(line, face, 122, 129, 2, 15)
  for (let i = 0; i < 12; i++) {
    const angle = i * TAU / 12
    const blade = (radius: number, sweep: number, z: number): Point => [
      Math.cos(angle + sweep) * radius, Math.sin(angle + sweep) * radius, z,
    ]
    const leading: Point[] = [], trailing: Point[] = []
    for (let j = 0; j <= 8; j++) {
      const t = j / 8
      leading.push(blade(35 + t * 82, t * .36, -14 + t * 23))
      trailing.push(blade(35 + t * 82, .17 + t * .5, 13 - t * 5))
      line(leading[j], trailing[j])
      if (j) {
        line(leading[j - 1], leading[j]); line(trailing[j - 1], trailing[j])
        face([leading[j - 1], leading[j], trailing[j], trailing[j - 1]])
      }
    }
    line(blade(129, 0, 2), blade(129, 0, 15))
  }
})

const airflow = sketch((line, face) => {
  // A wing section with a symmetric thickness profile and smooth streamlines.
  const foil = (t: number, side: number, z: number): Point => {
    const thickness = 5 * .18 * (0.2969 * Math.sqrt(t) - .126 * t - .3516 * t ** 2 + .2843 * t ** 3 - .1036 * t ** 4)
    return [-95 + t * 175, side * thickness * 175, z]
  }
  for (const z of [-55, 55]) {
    for (const side of [-1, 1]) {
      for (let i = 0; i < 30; i++) line(foil(i / 30, side, z), foil((i + 1) / 30, side, z))
    }
  }
  for (const side of [-1, 1]) {
    for (let i = 0; i < 30; i++) {
      face([foil(i / 30, side, -55), foil((i + 1) / 30, side, -55), foil((i + 1) / 30, side, 55), foil(i / 30, side, 55)])
    }
  }
  for (let i = 0; i <= 12; i++) {
    for (const side of [-1, 1]) line(foil(i / 12, side, -55), foil(i / 12, side, 55))
  }
  for (let row = -4; row <= 4; row++) {
    if (!row) continue
    for (let i = 0; i < 40; i++) {
      const point = (step: number): Point => {
        const x = -170 + step * 8.5
        return [x, row * 17 + Math.sign(row) * 22 * Math.exp(-(((x + 20) / 65) ** 2)), 0]
      }
      line(point(i), point(i + 1))
    }
  }
})

export const ENGINEERING_MODELS = [turbine, circuit, bridge, airflow]
